import React, { useState, useEffect, useRef } from 'react';
import {
  Container, Typography, Box, Grid,
  useTheme, alpha, Button, Card, CardContent,
  useMediaQuery, CircularProgress, Alert, Snackbar, Chip
} from '@mui/material';
import { motion, useScroll } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DescriptionIcon from '@mui/icons-material/Description';
import SettingsIcon from '@mui/icons-material/Settings';
import CodeIcon from '@mui/icons-material/Code';
import BugReportIcon from '@mui/icons-material/BugReport';
import StorageIcon from '@mui/icons-material/Storage';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ExploreIcon from '@mui/icons-material/Explore';
import BuildIcon from '@mui/icons-material/Build';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BusinessIcon from '@mui/icons-material/Business';
import EventIcon from '@mui/icons-material/Event';
import StraightenIcon from '@mui/icons-material/Straighten';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import GroupIcon from '@mui/icons-material/Group';
import TimelineIcon from '@mui/icons-material/Timeline';
import ListAltIcon from '@mui/icons-material/ListAlt';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import SelectivateLogo from '../components/SelectivateLogo';
import { useAuth } from '../contexts/AuthContext';
import { getUserProjects } from '../utils/api';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { getOverviewStatus, getCompletionMessage } from '../utils/projectOverviewService';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import LinearProgress from '@mui/material/LinearProgress';

const phases = [
  {
    id: 'discovery',
    name: 'Discover',
    color: '#4CAF50',
    icon: <ExploreIcon />,
    description: 'Initial Assessment & Discover',
    disabled: true,
    subprocesses: [
      { name: 'RFP Analysis', icon: <FindInPageIcon />, link: (projectId) => `/projects/${projectId}/discovery/rfp-analysis` },
      { name: 'Business Assessment', icon: <BusinessIcon />, link: (projectId) => `/projects/${projectId}/discovery/business-assessment` },
      { name: 'Current Landscape Analysis', icon: <DescriptionIcon />, link: (projectId) => `/projects/${projectId}/discovery/current-landscape-analysis` },
      { name: 'Requirements Gathering', icon: <AssignmentIcon />, link: (projectId) => `/projects/${projectId}/discovery/requirements-gathering` },
      { name: 'Stakeholder Interviews', icon: <GroupIcon />, link: (projectId) => `/projects/${projectId}/discovery/stakeholder-interviews` },
      { name: 'Technology Assessment', icon: <BuildIcon />, link: (projectId) => `/projects/${projectId}/discovery/technology-assessment` },
      { name: 'Feasibility Study', icon: <StraightenIcon />, link: (projectId) => `/projects/${projectId}/discovery/feasibility-study` }
    ]
  },
  {
    id: 'prepare',
    name: 'Prepare',
    color: '#F5821F',
    icon: <RocketLaunchIcon />,
    description: 'Project Planning & Strategy',
    subprocesses: [
      // { name: 'Technical Requirements', icon: <BuildIcon />, link: (projectId) => `/projects/${projectId}/prepare/technical-requirements` },
      // { name: 'Integration Map', icon: <DeviceHubIcon />, link: (projectId) => `/projects/${projectId}/prepare/integration-map` },
      { name: 'Workshop Schedule', icon: <EventIcon />, link: (projectId) => `/projects/${projectId}/prepare/workshop-schedule` },
      { name: 'RACI Matrix', icon: <AccountTreeIcon />, link: (projectId) => `/projects/${projectId}/prepare/raci-matrix` },
      { name: 'Project Charter', icon: <DescriptionIcon />, link: (projectId) => `/projects/${projectId}/prepare/templates` }
    ]
  },
  {
    id: 'explore',
    name: 'Explore',
    color: '#4DB6AC',
    icon: <ExploreIcon />,
    description: 'Business Process Discovery',
    subprocesses: [
      { name: 'Fit-to-Standard Presentation', icon: <DescriptionIcon />, link: (projectId) => `/projects/${projectId}/explore/fit-to-standard` },
      // { name: 'Current State Analysis', icon: <DescriptionIcon />, link: '#' },
      // { name: 'Future State Design', icon: <DescriptionIcon />, link: '#' },
      { name: 'Gap Analysis', icon: <DescriptionIcon />, link: '#' },
      { name: 'BPML & BPD', icon: <AccountTreeIcon />, link: (projectId) => `/projects/${projectId}/explore/bpml-bpd` },
      // { name: 'Process Mapping', icon: <DescriptionIcon />, link: '#' },
      // { name: 'Solution Design', icon: <DescriptionIcon />, link: (projectId) => `/projects/${projectId}/solution-proposal` }
    ]
  },
  {
    id: 'realize',
    name: 'Realize',
    color: '#9C27B0',
    icon: <BuildIcon />,
    description: 'Solution Build & Implementation',
    subprocesses: [
      { name: 'RICEFW List', icon: <ListAltIcon />, link: (projectId) => `/projects/${projectId}/realize/ricefw-list` },
      { 
        name: 'Documentation', 
        icon: <DescriptionIcon />,
        link: (projectId) => `/projects/${projectId}/realize/ricefw-list`,
        children: [
          { name: 'Functional Specs', link: (projectId) => `/projects/${projectId}/realize/ricefw-list` },
          { name: 'Technical Specs', link: (projectId) => `/projects/${projectId}/realize/ricefw-list` },
          { name: 'Config Docs', link: '#' }
        ]
      },
      // { name: 'Configuration', icon: <SettingsIcon />, link: '#' },
      // { name: 'Development', icon: <CodeIcon />, link: '#' },
      // { name: 'Data Migration', icon: <StorageIcon />, link: '#' },
    ]
  },
  {
    id: 'deploy',
    name: 'Deploy',
    color: '#FF5722',
    icon: <CloudUploadIcon />,
    description: 'Solution Deployment & Testing',
    subprocesses: [
      { name: 'Training', icon: <DescriptionIcon />, link: '#' },
      { name: 'Cutover Planning', icon: <SettingsIcon />, link: '#' },
      { name: 'Go-Live', icon: <RocketLaunchIcon />, link: '#' }
    ]
  },
  {
    id: 'run',
    name: 'Run',
    color: '#00BCD4',
    icon: <PlayCircleIcon />,
    description: 'Support & Optimization',
    subprocesses: [
      { name: 'Hypercare Support', icon: <SettingsIcon />, link: '#' },
      // { name: 'Performance Monitoring', icon: <SettingsIcon />, link: '#' },
      // { name: 'Continuous Improvement', icon: <SettingsIcon />, link: '#' },
      { name: 'Knowledge Transfer', icon: <DescriptionIcon />, link: '#' },
      { name: 'Application Support', icon: <SettingsIcon />, link: '#' }
    ]
  }
];

const HomePage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [hoveredPhase, setHoveredPhase] = useState(null);
  const [selectedProject, setSelectedProject] = useState('');
  const [userProjects, setUserProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const containerRef = useRef(null);
  const [overviewStatus, setOverviewStatus] = useState(null);
  const [loadingOverview, setLoadingOverview] = useState(false);

  // Screen size checks
  const isSmallScreen = useMediaQuery('(max-width:1400px)');
  const isExtraSmallScreen = useMediaQuery('(max-width:1000px)');
  const isLowHeightScreen = useMediaQuery('(max-height:800px)');
  
  // Scroll animation (disabled for now)
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });
  
  // Disabled rotation
  const rotate = 0; // useTransform(scrollYProgress, [0, 1], [0, 360]);

  // Fetch user's assigned projects on component mount
  useEffect(() => {
    if (currentUser) {
      fetchUserProjects();
    }
  }, [currentUser]);

  // Load selected project from localStorage on mount
  useEffect(() => {
    if (!currentUser) return;

    const savedProject = localStorage.getItem('selectedProject');
    if (savedProject) {
      try {
        const project = JSON.parse(savedProject);
        setSelectedProject(project.id ? project.id.toString() : savedProject);
      } catch (e) {
        setSelectedProject(savedProject);
      }
    }
  }, [currentUser]);

  // Listen for project changes from NavigationBar
  useEffect(() => {
    const handleProjectChangeEvent = () => {
      try {
        const savedProject = localStorage.getItem('selectedProject');
        if (savedProject) {
          const project = JSON.parse(savedProject);
          setSelectedProject(project.id ? project.id.toString() : savedProject);
          console.log('HomePage updated selected project from event:', project);
        } else {
          setSelectedProject('');
        }
      } catch (error) {
        console.error('Error updating project from event:', error);
      }
    };

    window.addEventListener('projectChanged', handleProjectChangeEvent);

    return () => {
      window.removeEventListener('projectChanged', handleProjectChangeEvent);
    };
  }, []);

  // Fetch overview status when project changes
  useEffect(() => {
    const fetchOverviewStatus = async () => {
      if (!selectedProject) {
        setOverviewStatus(null);
        return;
      }

      try {
        setLoadingOverview(true);
        const status = await getOverviewStatus(selectedProject);
        setOverviewStatus(status);
      } catch (error) {
        console.error('Error fetching overview status:', error);
        setOverviewStatus(null);
      } finally {
        setLoadingOverview(false);
      }
    };

    fetchOverviewStatus();
  }, [selectedProject]);

  const fetchUserProjects = async () => {
    if (!currentUser) return;

    setLoading(true);
    try {
      const response = await getUserProjects(currentUser.id);
      
      if (response.success) {
        setUserProjects(response.projects || []);
        
        // If there are no projects assigned, show an error
        if ((response.projects || []).length === 0) {
          setError('You have no projects assigned. Please contact an administrator.');
        }
      } else {
        setError(response.message || 'Failed to fetch your projects');
        showSnackbar('Failed to load your assigned projects', 'error');
      }
    } catch (error) {
      console.error('Error fetching user projects:', error);
      setError('An unexpected error occurred');
      showSnackbar('Error loading projects', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePhaseClick = (phase) => {
    // Only allow phase selection if a project is selected
    if (!selectedProject) {
      navigate('/settings');
      return;
    }
    if (phase.disabled) return;

    setSelectedPhase(selectedPhase?.id === phase.id ? null : phase);
  };

  const handleSubprocessClick = (link) => {
    // Only navigate if a project is selected
    if (!selectedProject) {
      navigate('/settings');
      return;
    }

    if (typeof link === 'function') {
      // If link is a function, call it with the selected project ID
      navigate(link(selectedProject));
    } else if (link.startsWith('/')) {
      // For backward compatibility with non-function links
      // Add the selected project as a query parameter
      if (link.includes('?')) {
        navigate(`${link}&project=${selectedProject}`);
      } else {
        navigate(`${link}?project=${selectedProject}`);
      }
    }
  };

  const showSnackbar = (message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  // Create path for arrow segments
  const createArrowPath = (index, total) => {
    const anglePerSegment = 360 / total;
    // Rotate the entire shape so Discover (index 0) is at the top
    // We need to offset by half a segment to center Discover at the top
    const rotationOffset = -anglePerSegment / 2;
    const startAngle = index * anglePerSegment + rotationOffset;
    const endAngle = (index + 1) * anglePerSegment + rotationOffset;
    const innerRadius = 160;
    const outerRadius = 240;
    const arrowWidth = 30;
    
    // Convert to radians
    const startRad = (startAngle - 90) * Math.PI / 180;
    const endRad = (endAngle - 90) * Math.PI / 180;
    
    // Calculate points
    const innerX1 = 300 + innerRadius * Math.cos(startRad);
    const innerY1 = 300 + innerRadius * Math.sin(startRad);
    const outerX1 = 300 + outerRadius * Math.cos(startRad);
    const outerY1 = 300 + outerRadius * Math.sin(startRad);
    
    const innerX2 = 300 + innerRadius * Math.cos(endRad);
    const innerY2 = 300 + innerRadius * Math.sin(endRad);
    const outerX2 = 300 + outerRadius * Math.cos(endRad);
    const outerY2 = 300 + outerRadius * Math.sin(endRad);
    
    // Arrow point
    const midRad = ((startRad + endRad) / 2);
    const arrowTipX = 300 + (outerRadius + arrowWidth) * Math.cos(midRad);
    const arrowTipY = 300 + (outerRadius + arrowWidth) * Math.sin(midRad);
    
    return `
      M ${innerX1} ${innerY1}
      A ${innerRadius} ${innerRadius} 0 0 1 ${innerX2} ${innerY2}
      L ${outerX2} ${outerY2}
      L ${arrowTipX} ${arrowTipY}
      L ${outerX1} ${outerY1}
      Z
    `;
  };

  const getPhasePosition = (index, total) => {
    const anglePerSegment = 360 / total;
    // Apply the same rotation offset as the arrow segments
    const rotationOffset = -anglePerSegment / 2;
    const angle = (index * anglePerSegment + anglePerSegment / 2 + rotationOffset - 90) * Math.PI / 180;
    const radius = 200;
    return {
      x: 300 + radius * Math.cos(angle),
      y: 300 + radius * Math.sin(angle)
    };
  };

  // Get project name from ID
  const getProjectName = (projectId) => {
    const project = userProjects.find(p => p.id.toString() === projectId.toString());
    return project ? project.name : 'Unknown Project';
  };

  return (
    <Box 
      ref={containerRef}
      sx={{ 
        minHeight: '100vh', // Reduced height since scroll effect is disabled
        py: 6, 
        backgroundColor: '#121212', 
        overflow: 'hidden' 
      }}
    >
      <Container maxWidth="lg">
        {/* Circular Arrow Diagram - Now requires project selection */}
        <Box
          sx={{
            position: isLowHeightScreen ? 'relative' : 'sticky',
            top: isLowHeightScreen ? 'auto' : '25vh',
            transform: isLowHeightScreen ? 'none' : 'translateY(-50%)',
            width: '100%',
            height: isLowHeightScreen ? 'auto' : 600,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            mb: 4,
            pt: 0,
            mt: 0,
            filter: !selectedProject ? 'grayscale(0.8) opacity(0.7)' : 'none',
            transition: 'filter 0.3s ease'
          }}
        >
          <motion.svg
            width={isLowHeightScreen ? 500 : 600}
            height={isLowHeightScreen ? 500 : 600}
            viewBox="0 0 600 600"
            style={{ 
              position: 'relative', // Changed from absolute to relative
              rotate,
              transform: isLowHeightScreen ? 'scale(0.85)' : 'none' // Scale down for low height screens
            }}
          >
            {/* Arrow segments */}
            {phases.map((phase, index) => (
              <motion.path
                key={phase.id}
                d={createArrowPath(index, phases.length)}
                fill={selectedPhase?.id === phase.id ? phase.color : `${phase.color}88`}
                stroke="#fff"
                strokeWidth="2"
                style={{
                  cursor: selectedProject && !phase.disabled ? 'pointer' : 'not-allowed',
                  opacity: phase.disabled ? 0.55 : 1,
                  transition: 'fill 0.3s ease'
                }}
                onClick={() => handlePhaseClick(phase)}
                onMouseEnter={() => setHoveredPhase(phase)}
                onMouseLeave={() => setHoveredPhase(null)}
                whileHover={{ scale: selectedProject && !phase.disabled ? 1.05 : 1 }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.1 }}
              />
            ))}
            
            {/* Center circle */}
            <circle
              cx="300"
              cy="300"
              r="140"
              fill="#1D2B3F"
              stroke="#F5821F"
              strokeWidth="4"
            />
            
            {/* Phase labels */}
            {phases.map((phase, index) => {
              const position = getPhasePosition(index, phases.length);
              return (
                <g key={`label-${phase.id}`}>
                  <text
                    x={position.x}
                    y={position.y}
                    textAnchor="middle"
                    fill="white"
                    fontSize="18"
                    fontWeight="bold"
                    style={{ 
                      pointerEvents: 'none',
                      filter: selectedPhase?.id === phase.id ? 'drop-shadow(0 0 8px rgba(0,0,0,0.5))' : 'none'
                    }}
                  >
                    {phase.name}
                  </text>
                </g>
              );
            })}
          </motion.svg>

          {/* Center content (non-rotating) - With Selectivate Logo and project info */}
          <Box
            sx={{
              position: 'absolute',
              width: 220,
              height: 220,
              borderRadius: '50%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              zIndex: 2
            }}
          >
            <SelectivateLogo variant="circle" size="large" />
          </Box>
        </Box>

        {/* Navigation message - below the cycle */}
        <Box sx={{ textAlign: 'center', mb: 6, mt: 2 }}>
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              maxWidth: 600,
              mx: 'auto'
            }}
          >
            {selectedProject ?
              'Navigate through your SAP S/4HANA implementation phases' :
              'Please select a project from Settings (gear icon) to get started'
            }
          </Typography>
        </Box>

        {/* Project Overview Status Card */}
        {selectedProject && overviewStatus && !loadingOverview && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Card
              sx={{
                mb: 6,
                p: 3,
                background: `linear-gradient(135deg, ${alpha('#F5821F', 0.1)} 0%, ${alpha('#F5821F', 0.05)} 100%)`,
                border: `1px solid ${alpha('#F5821F', 0.3)}`,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: `0 8px 16px ${alpha('#F5821F', 0.2)}`
                }
              }}
              onClick={() => navigate(`/projects/${selectedProject}/overview`)}
            >
              <CardContent>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <DescriptionIcon sx={{ fontSize: 32, color: '#F5821F', mr: 2 }} />
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 600, color: '#F5821F' }}>
                        Project Overview
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Foundation before SAP phases
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={`${overviewStatus.completion_percentage}% Complete`}
                    color={overviewStatus.is_complete ? 'success' : 'warning'}
                    icon={overviewStatus.is_complete ? <CheckCircleIcon /> : <WarningIcon />}
                    sx={{ fontWeight: 600 }}
                  />
                </Box>

                {/* Progress Bar */}
                <Box sx={{ mb: 3 }}>
                  <LinearProgress
                    variant="determinate"
                    value={overviewStatus.completion_percentage}
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      backgroundColor: alpha('#F5821F', 0.1),
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: overviewStatus.is_complete ? '#4CAF50' : '#F5821F',
                        borderRadius: 5
                      }
                    }}
                  />
                </Box>

                {/* Completion Message */}
                <Alert
                  severity={overviewStatus.is_complete ? 'success' : 'info'}
                  sx={{ mb: 3 }}
                  icon={overviewStatus.is_complete ? <CheckCircleIcon /> : <WarningIcon />}
                >
                  {getCompletionMessage(overviewStatus.completion_percentage, overviewStatus.is_complete)}
                </Alert>

                {/* Section Status Grid */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {[
                    { id: 'sow_insights', label: 'Project Insights', path: 'sow-insights' },
                    { id: 'project_scope', label: 'Project Scope', path: 'scope' },
                    { id: 'requirements', label: 'Project Requirements', path: 'requirements' },
                    { id: 'stakeholders', label: 'Project Team', path: 'stakeholders' },
                    { id: 'timeline', label: 'Timeline', path: 'timeline' }
                  ].map((section) => {
                    const sectionStatus = overviewStatus.sections?.[section.id] || 'not_started';
                    const icon = sectionStatus === 'completed' ? <CheckCircleIcon sx={{ color: '#4CAF50' }} /> :
                                 sectionStatus === 'in_progress' ? <WarningIcon sx={{ color: '#F5821F' }} /> :
                                 <ErrorIcon sx={{ color: '#666' }} />;

                    return (
                      <Grid item xs={12} sm={6} key={section.id}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            p: 2,
                            borderRadius: 2,
                            backgroundColor: sectionStatus === 'completed' ? alpha('#4CAF50', 0.1) :
                                             sectionStatus === 'in_progress' ? alpha('#F5821F', 0.1) :
                                             alpha('#666', 0.1),
                            border: `1px solid ${sectionStatus === 'completed' ? alpha('#4CAF50', 0.3) :
                                                  sectionStatus === 'in_progress' ? alpha('#F5821F', 0.3) :
                                                  alpha('#666', 0.3)}`
                          }}
                        >
                          {icon}
                          <Typography variant="body2" sx={{ ml: 1, fontWeight: 500 }}>
                            {section.label}
                          </Typography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/projects/${selectedProject}/overview`);
                    }}
                    sx={{
                      backgroundColor: '#F5821F',
                      '&:hover': {
                        backgroundColor: alpha('#F5821F', 0.8)
                      }
                    }}
                  >
                    {overviewStatus.is_complete ? 'View Overview' : 'Continue Setup'}
                  </Button>
                  {overviewStatus.is_complete && (
                    <Button
                      variant="outlined"
                      startIcon={<RocketLaunchIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/projects/${selectedProject}/discovery/rfp-analysis`);
                      }}
                      sx={{
                        borderColor: '#4CAF50',
                        color: '#4CAF50',
                        '&:hover': {
                          borderColor: '#4CAF50',
                          backgroundColor: alpha('#4CAF50', 0.1)
                        }
                      }}
                    >
                      Start SAP Phases
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Selected Phase Details */}
        {selectedPhase && !selectedPhase.disabled && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            transition={{ duration: 0.4 }}
          >
            <Box
              sx={{
                mb: 8,
                p: 4,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(selectedPhase.color, 0.1)} 0%, ${alpha(selectedPhase.color, 0.05)} 100%)`,
                border: `1px solid ${alpha(selectedPhase.color, 0.2)}`,
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              {/* Phase Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                {React.cloneElement(selectedPhase.icon, {
                  sx: { fontSize: 36, color: selectedPhase.color, mr: 2 }
                })}
                <Box>
                  <Typography variant="h4" sx={{ color: selectedPhase.color, fontWeight: 600 }}>
                    {selectedPhase.name} Phase
                  </Typography>
                  <Typography variant="subtitle1" sx={{ color: 'text.secondary' }}>
                    {selectedPhase.description}
                  </Typography>
                </Box>
              </Box>

              {/* Subprocesses Grid */}
              <Grid container spacing={3}>
                {selectedPhase.subprocesses.map((subprocess, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Card
                        sx={{
                          height: '100%',
                          background: 'linear-gradient(135deg, #1e1e1e 0%, #252525 100%)',
                          border: `1px solid ${alpha(selectedPhase.color, 0.2)}`,
                          cursor: !selectedProject ? 'not-allowed' : subprocess.link !== '#' ? 'pointer' : 'default',
                          transition: 'all 0.3s ease',
                          opacity: !selectedProject ? 0.7 : 1,
                          '&:hover': {
                            transform: selectedProject && subprocess.link !== '#' ? 'translateY(-5px)' : 'none',
                            boxShadow: selectedProject && subprocess.link !== '#' ? `0 5px 15px ${alpha(selectedPhase.color, 0.2)}` : 'none'
                          }
                        }}
                        onClick={() => handleSubprocessClick(subprocess.link)}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            {React.cloneElement(subprocess.icon, {
                              sx: { fontSize: 28, color: selectedPhase.color, mr: 2 }
                            })}
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {subprocess.name}
                            </Typography>
                          </Box>
                          
                          {/* For Documentation subprocess with children */}
                          {subprocess.children && (
                            <Box sx={{ pl: 4 }}>
                              {subprocess.children.map((child, childIndex) => (
                                <Button
                                  key={childIndex}
                                  variant="outlined"
                                  size="small"
                                  startIcon={<ArrowForwardIcon />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSubprocessClick(child.link);
                                  }}
                                  sx={{
                                    display: 'block',
                                    width: '100%',
                                    mb: 1,
                                    textAlign: 'left',
                                    borderColor: alpha(selectedPhase.color, 0.5),
                                    color: 'text.primary',
                                    opacity: !selectedProject ? 0.7 : 1,
                                    minHeight: '36px',
                                    justifyContent: 'flex-start',
                                    '&:hover': {
                                      borderColor: selectedProject ? selectedPhase.color : alpha(selectedPhase.color, 0.5),
                                      backgroundColor: selectedProject ? alpha(selectedPhase.color, 0.1) : 'transparent'
                                    }
                                  }}
                                  disabled={!selectedProject}
                                >
                                  {child.name}
                                </Button>
                              ))}
                            </Box>
                          )}

                          
                          {!selectedProject && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                display: 'block', 
                                color: selectedPhase.color,
                                mt: 2,
                                fontStyle: 'italic'
                              }}
                            >
                              Please select a project first
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>

              {/* Close button */}
              <Button
                variant="outlined"
                size="small"
                onClick={() => setSelectedPhase(null)}
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  borderColor: alpha(selectedPhase.color, 0.5),
                  color: selectedPhase.color,
                  '&:hover': {
                    borderColor: selectedPhase.color,
                    backgroundColor: alpha(selectedPhase.color, 0.1)
                  }
                }}
              >
                Close
              </Button>
            </Box>
          </motion.div>
        )}

        {/* Feature Showcase Section */}
        <Box sx={{ mb: 4, mt: -4 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              mb: 4,
              textAlign: 'center',
              color: theme.palette.primary.main
            }}
          >
            Platform Features
          </Typography>
          
          <Grid container spacing={3}>
            {[
              {
                title: 'AI-Powered Documentation',
                description: 'Generate comprehensive SAP functional specifications using advanced AI technology with ABAP code, configuration paths, and process flows',
                icon: <AutoAwesomeIcon />,
                color: theme.palette.primary.main,
                link: '/generate',
                requiresProject: true
              },
              {
                title: 'Project Document Intelligence',
                description: 'AI-powered analysis to automatically extract project details, requirements, and insights from project documents',
                icon: <FindInPageIcon />,
                color: '#4CAF50',
                link: (projectId) => `/projects/${projectId}/discovery/rfp-analysis`,
                requiresProject: true
              },
              {
                title: '6-Phase Implementation',
                description: 'Complete SAP implementation lifecycle management from Discover to Run phases with stakeholder tracking, RACI matrix, and timeline management',
                icon: <AccountTreeIcon />,
                color: '#9C27B0',
                link: '/home',
                requiresProject: false
              }
            ].map((feature, index) => (
              <Grid item xs={12} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.2 + 0.5 }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      minHeight: 280,
                      display: 'flex',
                      flexDirection: 'column',
                      background: 'linear-gradient(135deg, #1e1e1e 0%, #252525 100%)',
                      border: `1px solid ${alpha(feature.color, 0.2)}`,
                      cursor: (!feature.requiresProject || selectedProject) ? 'pointer' : 'not-allowed',
                      transition: 'all 0.3s ease',
                      opacity: (feature.requiresProject && !selectedProject) ? 0.7 : 1,
                      '&:hover': {
                        transform: (!feature.requiresProject || selectedProject) ? 'translateY(-5px)' : 'none',
                        boxShadow: (!feature.requiresProject || selectedProject) ? `0 10px 20px ${alpha(feature.color, 0.2)}` : 'none'
                      }
                    }}
                    onClick={() => {
                      if (feature.requiresProject && !selectedProject) {
                        navigate('/settings');
                      } else {
                        // Check if link is a function or string
                        if (typeof feature.link === 'function') {
                          navigate(feature.link(selectedProject));
                        } else {
                          navigate(feature.link + (feature.requiresProject ? `?project=${selectedProject}` : ''));
                        }
                      }
                    }}
                  >
                    <CardContent sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      p: 3
                    }}>
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: 2,
                          background: alpha(feature.color, 0.1),
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          mb: 2
                        }}
                      >
                        {React.cloneElement(feature.icon, {
                          sx: { fontSize: 32, color: feature.color }
                        })}
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', minHeight: 60 }}>
                        {feature.description}
                      </Typography>
                      
                      {feature.requiresProject && !selectedProject && (
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            display: 'block', 
                            color: feature.color,
                            mt: 2,
                            fontStyle: 'italic'
                          }}
                        >
                          Requires project selection
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Call to Action */}
        <Box
          sx={{
            textAlign: 'center',
            py: 6,
            px: 4,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            Ready to streamline your SAP implementation?
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4, maxWidth: 600, mx: 'auto' }}>
            Start generating professional SAP documentation today with our AI-powered platform.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<RocketLaunchIcon />}
            onClick={() => {
              if (selectedProject) {
                navigate(`/generate?project=${selectedProject}`);
              } else {
                navigate('/settings');
              }
            }}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 2,
              boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.25)}`,
              '&:hover': {
                boxShadow: `0 10px 20px ${alpha(theme.palette.primary.main, 0.3)}`
              }
            }}
          >
            {selectedProject ? 'Get Started Now' : 'Select a Project'}
          </Button>
        </Box>
      </Container>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default HomePage;
